import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login, reset } from "../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Login = () => {
  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const { email, password } = form;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user, success, error, message, loading } = useSelector((state) => state.auth);

  // Prefill tenantHint from URL query params if present (e.g. ?tenantHint=... or ?tenantId=...)
  // (Removed tenantHint field from UI — no prefill behavior needed)

  const [showLongWait, setShowLongWait] = useState(false);

  useEffect(() => {
    let t;
    if (loading) {
      setShowLongWait(false);
      // Si a los 8 segundos sigue cargando, mostrar advertencia
      t = setTimeout(() => setShowLongWait(true), 8000);
    } else {
      setShowLongWait(false);
    }
    return () => clearTimeout(t);
  }, [loading]);

  const getErrorText = useCallback(() => {
    if (!message) return 'Correo o contraseña incorrectos';
    if (typeof message === 'string') return message;
    if (message?.message) return message.message;
    return 'Correo o contraseña incorrectos';
  }, [message]);

  useEffect(() => {
    if (error) {
      toast.error(getErrorText());
      const t = setTimeout(() => dispatch(reset()), 3000);
      return () => clearTimeout(t);
    }
    if (success || user) {
      // Si el dispositivo es móvil, ir directamente a Ventas para flujo POS
      // Robust mobile detection: prefer userAgent (works in WebViews) and fallback to matchMedia
      let isMobile = false;
      try {
        try {
          // userAgent check is more reliable in embedded webviews
          isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
        } catch (e) {
          isMobile = false;
        }
        if (!isMobile && typeof window !== 'undefined' && window.matchMedia) {
          isMobile = window.matchMedia('(max-width: 768px)').matches;
        }
      } catch (e) {
        isMobile = false;
      }
      // Navigate using replace to avoid back-button confusion
      if (isMobile) navigate('/sales', { replace: true });
      else navigate('/', { replace: true }); // ✅ Redirige al dashboard en desktop
      dispatch(reset());
    }
  }, [error, success, user, message, navigate, dispatch, getErrorText]);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Completa todos los campos");
      return;
    }
    const payload = { email, password };
    dispatch(login(payload));
  };

  return (
    <div style={containerStyle}>
      <div style={formCardStyle}>
        <h1 style={titleStyle}>🔐 Iniciar Sesión</h1>
        <p style={subtitleStyle}>Sistemas Ventio Pos</p>

        <form onSubmit={handleSubmit}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>📧 Email</label>
            <input
              type="email"
              placeholder="tu@email.com"
              name="email"
              value={email}
              onChange={onChange}
              style={inputStyle}
              required
            />
          </div>

          <div style={formGroupStyle}>
            <label style={labelStyle}>🔒 Contraseña</label>
            <input
              type="password"
              placeholder="••••••••"
              name="password"
              value={password}
              onChange={onChange}
              style={inputStyle}
              required
            />
          </div>

          {/* Campo comercio removido por requerimiento */}

          <button type="submit" style={btnLoginStyle} disabled={loading}>
            {loading ? 'Ingresando…' : '✅ Iniciar Sesión'}
          </button>

          <div style={helperRowStyle}>
            <a href="/website/forgot.html" style={linkStyle}>¿Olvidaste tu contraseña?</a>
          </div>

          {/* Botón DEV removido */}

          {showLongWait && (
            <div style={{ marginTop: 10, padding: 10, background: '#fff3cd', color: '#856404', borderRadius: 8, fontSize: 13, border: '1px solid #ffeeba' }}>
              ⚠️ La conexión está tardando más de lo esperado.<br />
              <div style={{ marginTop: 4 }}>
                1. Verifica que el backend esté corriendo en el puerto 5000.<br />
                2. Si persiste, intenta <span onClick={() => window.location.reload()} style={{ fontWeight: 'bold', textDecoration: 'underline', cursor: 'pointer' }}>recargar la página</span>.
              </div>
            </div>
          )}

          {error && (
            <div style={errorBoxStyle} role="alert">
              {getErrorText()}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

// Estilos
const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  fontFamily: 'Arial, sans-serif',
  padding: '20px',
};

const formCardStyle = {
  background: 'white',
  padding: '40px',
  borderRadius: '16px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  width: '100%',
  maxWidth: '450px',
};

const titleStyle = {
  textAlign: 'center',
  color: '#333',
  marginBottom: '10px',
  fontSize: '32px',
};

const subtitleStyle = {
  textAlign: 'center',
  color: '#666',
  marginBottom: '30px',
  fontSize: '16px',
};

const formGroupStyle = {
  marginBottom: '20px',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#333',
  fontWeight: '600',
  fontSize: '14px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 15px',
  borderRadius: '8px',
  border: '2px solid #e0e0e0',
  fontSize: '16px',
  transition: 'border-color 0.3s',
  boxSizing: 'border-box',
  outline: 'none',
};

const btnLoginStyle = {
  width: '100%',
  padding: '14px',
  borderRadius: '8px',
  border: 'none',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  fontSize: '18px',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s',
  marginTop: '10px',
};

const helperRowStyle = {
  marginTop: '12px',
  textAlign: 'center',
};

const linkStyle = {
  color: '#4f46e5',
  textDecoration: 'none',
  fontWeight: 600,
};

const errorBoxStyle = {
  marginTop: '16px',
  padding: '10px 12px',
  borderRadius: '8px',
  background: '#fee2e2',
  color: '#991b1b',
  border: '1px solid #fecaca',
  fontSize: '14px',
  fontWeight: 600,
};

export default Login;
